import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, Shield, CheckCircle, Loader, AlertCircle, Calendar } from 'lucide-react';
import { formatCurrency } from '../../utils/calculations';
interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  amount: number;
  dueDate: string;
  onSuccess: () => void;
}
type Step = 'method' | 'processing' | 'success';
export function PaymentModal({
  isOpen,
  onClose,
  amount,
  dueDate,
  onSuccess
}: PaymentModalProps) {
  const [step, setStep] = useState<Step>('method');
  const [paymentMethod, setPaymentMethod] = useState<'auto-debit' | 'card' | 'bank'>('auto-debit');
  const handlePay = () => {
    setStep('processing');
    // Simulate payment processing
    setTimeout(() => {
      setStep('success');
      setTimeout(() => {
        onSuccess();
        onClose();
        resetModal();
      }, 2000);
    }, 2000);
  };
  const resetModal = () => {
    setStep('method');
    setPaymentMethod('auto-debit');
  };
  const handleClose = () => {
    if (step !== 'processing') {
      onClose();
      setTimeout(resetModal, 300);
    }
  };
  return <AnimatePresence data-id="element-1619">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={handleClose} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" data-id="element-1620" />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1621">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-id="element-1622">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10" data-id="element-1623">
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-1624">
                  {step === 'success' ? 'Payment Successful!' : 'Pay Rent'}
                </h2>
                {step !== 'processing' && <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1625">
                    <X className="h-5 w-5 text-gray-500" data-id="element-1626" />
                  </button>}
              </div>

              {/* Content */}
              <div className="p-6" data-id="element-1627">
                {step === 'method' && <div className="space-y-6" data-id="element-1628">
                    {/* Payment Amount */}
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 text-center border border-blue-200" data-id="element-1629">
                      <p className="text-sm text-blue-700 mb-2" data-id="element-1630">Amount Due</p>
                      <p className="text-4xl font-bold text-blue-900 mb-2" data-id="element-1631">
                        {formatCurrency(amount)}
                      </p>
                      <div className="flex items-center justify-center text-sm text-blue-700" data-id="element-1632">
                        <Calendar className="h-4 w-4 mr-2" data-id="element-1633" />
                        Due:{' '}
                        {new Date(dueDate).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                      </div>
                    </div>

                    {/* Payment Methods */}
                    <div data-id="element-1634">
                      <h3 className="font-semibold text-gray-900 mb-4" data-id="element-1635">
                        Select Payment Method
                      </h3>
                      <div className="space-y-3" data-id="element-1636">
                        {/* Auto-Debit */}
                        <button onClick={() => setPaymentMethod('auto-debit')} className={`w-full p-4 rounded-xl border-2 transition-all text-left ${paymentMethod === 'auto-debit' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1637">
                          <div className="flex items-start" data-id="element-1638">
                            <div className={`h-5 w-5 rounded-full border-2 mr-3 mt-0.5 flex items-center justify-center ${paymentMethod === 'auto-debit' ? 'border-blue-600' : 'border-gray-300'}`} data-id="element-1639">
                              {paymentMethod === 'auto-debit' && <div className="h-2.5 w-2.5 bg-blue-600 rounded-full" data-id="element-1640" />}
                            </div>
                            <div className="flex-1" data-id="element-1641">
                              <div className="flex items-center justify-between mb-1" data-id="element-1642">
                                <span className="font-semibold text-gray-900" data-id="element-1643">
                                  Auto-Debit (Recommended)
                                </span>
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium" data-id="element-1644">
                                  No Fees
                                </span>
                              </div>
                              <p className="text-sm text-gray-600" data-id="element-1645">
                                Automatic deduction from your linked salary
                                account
                              </p>
                              <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-1646">
                                <Shield className="h-3 w-3 mr-1" data-id="element-1647" />
                                Secure & Verified
                              </div>
                            </div>
                          </div>
                        </button>

                        {/* Card Payment */}
                        <button onClick={() => setPaymentMethod('card')} className={`w-full p-4 rounded-xl border-2 transition-all text-left ${paymentMethod === 'card' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1648">
                          <div className="flex items-start" data-id="element-1649">
                            <div className={`h-5 w-5 rounded-full border-2 mr-3 mt-0.5 flex items-center justify-center ${paymentMethod === 'card' ? 'border-blue-600' : 'border-gray-300'}`} data-id="element-1650">
                              {paymentMethod === 'card' && <div className="h-2.5 w-2.5 bg-blue-600 rounded-full" data-id="element-1651" />}
                            </div>
                            <div className="flex-1" data-id="element-1652">
                              <div className="flex items-center justify-between mb-1" data-id="element-1653">
                                <span className="font-semibold text-gray-900" data-id="element-1654">
                                  Debit/Credit Card
                                </span>
                                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium" data-id="element-1655">
                                  1.5% Fee
                                </span>
                              </div>
                              <p className="text-sm text-gray-600" data-id="element-1656">
                                Pay with Visa, Mastercard, or Verve
                              </p>
                            </div>
                          </div>
                        </button>

                        {/* Bank Transfer */}
                        <button onClick={() => setPaymentMethod('bank')} className={`w-full p-4 rounded-xl border-2 transition-all text-left ${paymentMethod === 'bank' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1657">
                          <div className="flex items-start" data-id="element-1658">
                            <div className={`h-5 w-5 rounded-full border-2 mr-3 mt-0.5 flex items-center justify-center ${paymentMethod === 'bank' ? 'border-blue-600' : 'border-gray-300'}`} data-id="element-1659">
                              {paymentMethod === 'bank' && <div className="h-2.5 w-2.5 bg-blue-600 rounded-full" data-id="element-1660" />}
                            </div>
                            <div className="flex-1" data-id="element-1661">
                              <div className="flex items-center justify-between mb-1" data-id="element-1662">
                                <span className="font-semibold text-gray-900" data-id="element-1663">
                                  Bank Transfer
                                </span>
                                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium" data-id="element-1664">
                                  No Fees
                                </span>
                              </div>
                              <p className="text-sm text-gray-600" data-id="element-1665">
                                Transfer directly from your bank account
                              </p>
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>

                    {/* Info Box */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-1666">
                      <div className="flex items-start" data-id="element-1667">
                        <Shield className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" data-id="element-1668" />
                        <div className="text-sm text-blue-800" data-id="element-1669">
                          <p className="font-medium mb-1" data-id="element-1670">Secure Payment</p>
                          <p data-id="element-1671">
                            Your payment is protected by bank-grade encryption
                            and processed through secure payment gateways.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3" data-id="element-1672">
                      <button onClick={handleClose} className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1673">
                        Cancel
                      </button>
                      <button onClick={handlePay} className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center" data-id="element-1674">
                        <CreditCard className="h-5 w-5 mr-2" data-id="element-1675" />
                        Pay Now
                      </button>
                    </div>
                  </div>}

                {step === 'processing' && <div className="py-12 text-center" data-id="element-1676">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6" data-id="element-1677">
                      <Loader className="h-8 w-8 text-blue-600 animate-spin" data-id="element-1678" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1679">
                      Processing Payment...
                    </h3>
                    <p className="text-gray-600" data-id="element-1680">
                      Please wait while we process your payment securely.
                    </p>
                  </div>}

                {step === 'success' && <div className="py-12 text-center" data-id="element-1681">
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 200,
                damping: 15
              }} className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6" data-id="element-1682">
                      <CheckCircle className="h-8 w-8 text-green-600" data-id="element-1683" />
                    </motion.div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1684">
                      Payment Successful!
                    </h3>
                    <p className="text-gray-600 mb-6" data-id="element-1685">
                      Your rent payment of {formatCurrency(amount)} has been
                      processed successfully.
                    </p>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-md mx-auto" data-id="element-1686">
                      <p className="text-sm text-green-800" data-id="element-1687">
                        A receipt has been sent to your email. Your next payment
                        is due on{' '}
                        {new Date(new Date(dueDate).setMonth(new Date(dueDate).getMonth() + 1)).toLocaleDateString()}
                      </p>
                    </div>
                  </div>}
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}